import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AddOrientationSession, UpdateOrientationDay } from 'src/app/compliance/models/add-orientation-session.model';
import { ScheduleOrientationSessionType } from 'src/app/compliance/models/schedule-orientation-session-type';
import {
    CandidateAvailabilityDate,
    MultiBundleStatus as LegacyMultiBundleStatus,
    ScheduleOrientation,
    ScheduleOrientationPresetDate
} from 'src/app/compliance/models/schedule-orientation.model';
import { FetchArgs, PagingToken } from 'src/app/shared/models/core';
import { FeatureFlag } from '../../../shared/models/enums/feature-flag';
import { ScheduleOrientationSessionTypesEnum } from '../../api/enums/schedule-orientation-session-types-enum.enum';
import { AddScheduleOrientationSessionDto } from '../../api/models/add-schedule-orientation-session-dto.model';
import { CandidateAvailabilityDateDto } from '../../api/models/candidate-availability-date-dto.model';
import { FetchArguments2OfPerDiemScheduleOrientationFilter } from '../../api/models/fetch-arguments2of-per-diem-schedule-orientation-filter.model';
import { MultiBundleStatus } from '../../api/models/multi-bundle-status.model';
import { PagingTokenOfScheduleOrientationDto } from '../../api/models/paging-token-of-schedule-orientation-dto.model';
import { ScheduleOrientationDto } from '../../api/models/schedule-orientation-dto.model';
import { ScheduleOrientationPresetDateDto } from '../../api/models/schedule-orientation-preset-date-dto.model';
import { ScheduleOrientationSessionTypeDto } from '../../api/models/schedule-orientation-session-type-dto.model';
import { UpdateOrientationDto } from '../../api/models/update-orientation-dto.model';
import { ScheduleOrientationClient } from '../../api/schedule-orientation-client.service';
import { ScheduleOrientationService } from '../../compliance-report/services/schedule-orientation.service';
import { NSwagMigrationService } from './nswag-migration.service';

// 27 endpoints
@Injectable({ providedIn: 'root' })
export class ScheduleOrientationNSwagMigrationService extends NSwagMigrationService {
    protected readonly className = 'ScheduleOrientationNSwagMigrationService';
    protected readonly featureFlag = FeatureFlag.NSwagMigrationScheduleOrientationService;
    private readonly legacy = inject(ScheduleOrientationService);
    private readonly scheduleOrientationClient = inject(ScheduleOrientationClient);

    // ** ScheduleOrientationService.getScheduleOrientation, ScheduleOrientationController.GetOrientation
    getScheduleOrientation(contractId: number): Observable<ScheduleOrientation | ScheduleOrientationDto> {
        return this.invoke(x => x
            ? this.legacy.getScheduleOrientation(contractId)
            : this.scheduleOrientationClient.getOrientation(contractId));
    }

    // ** ScheduleOrientationService.getScheduleOrientationByScheduleOrientationId, ScheduleOrientationController.GetOrientationByScheduleOrientationId
    getScheduleOrientationByScheduleOrientationId(orientationId: number): Observable<ScheduleOrientation | ScheduleOrientationDto> {
        return this.invoke(x => x
            ? this.legacy.getScheduleOrientationByScheduleOrientationId(orientationId)
            : this.scheduleOrientationClient.getOrientationByScheduleOrientationId(orientationId));
    }

    // ** ScheduleOrientationService.getScheduleOrientationSessions, ScheduleOrientationController.GetOrientations
    getScheduleOrientationSessions(contractId: number, args: FetchArgs | FetchArguments2OfPerDiemScheduleOrientationFilter): Observable<PagingToken<ScheduleOrientation[]> | PagingTokenOfScheduleOrientationDto> {
        return this.invoke(x => x
            ? this.legacy.getScheduleOrientationSessions(contractId, args as FetchArgs)
            : this.scheduleOrientationClient.getOrientations(contractId, args as FetchArguments2OfPerDiemScheduleOrientationFilter));
    }

    // ** ScheduleOrientationService.getActionRequiredOrientationSessionsCount, ScheduleOrientationController.GetActionRequiredOrientationSessionsCount
    getActionRequiredOrientationSessionsCount(contractId: number): Observable<number> {
        return this.invoke(x => x
            ? this.legacy.getActionRequiredOrientationSessionsCount(contractId)
            : this.scheduleOrientationClient.getActionRequiredOrientationSessionsCount(contractId));
    }

    // ** ScheduleOrientationService.postScheduleOrientation, ScheduleOrientationController.ScheduleOrientation
    postScheduleOrientation(contractId: number, note: string): Observable<ScheduleOrientation | ScheduleOrientationDto> {
        return this.invoke(x => x
            ? this.legacy.postScheduleOrientation(contractId, note)
            : this.scheduleOrientationClient.scheduleOrientationPOST(contractId, note));
    }

    // ** ScheduleOrientationService.postAddNewOrientation, ScheduleOrientationController.ScheduleOrientationSession
    postAddNewOrientation(contractId: number, newOrientation: AddOrientationSession[] | AddScheduleOrientationSessionDto[]): Observable<unknown> {
        return this.invoke(x => x
            ? this.legacy.postAddNewOrientation(contractId, newOrientation)
            : this.scheduleOrientationClient.scheduleOrientationSession(contractId, newOrientation as AddScheduleOrientationSessionDto[]));
    }

    // ** ScheduleOrientationService.postScheduleOrientationDateForCandidate, ScheduleOrientationController.ScheduleOrientationDate
    postScheduleOrientationDateForCandidate(scheduleOrientationId: number, date: Date | string): Observable<ScheduleOrientation | ScheduleOrientationDto> {
        return this.invoke(x => x
            ? this.legacy.postScheduleOrientationDateForCandidate(scheduleOrientationId, date)
            : this.scheduleOrientationClient.scheduleOrientationDatePOST(scheduleOrientationId, new Date(date)));
    }

    // ** ScheduleOrientationService.putScheduleOrientationDateForCandidate, ScheduleOrientationController.ScheduleOrientationDate
    putScheduleOrientationDateForCandidate(dateId: number): Observable<ScheduleOrientation | ScheduleOrientationDto> {
        return this.invoke(x => x
            ? this.legacy.putScheduleOrientationDateForCandidate(dateId)
            : this.scheduleOrientationClient.scheduleOrientationDatePUT(dateId));
    }

    // ** ScheduleOrientationService.removeScheduleOrientation, ScheduleOrientationController.ScheduleOrientation
    removeScheduleOrientation(contractId: number): Observable<unknown | void> {
        return this.invoke(x => x
            ? this.legacy.removeScheduleOrientation(contractId)
            : this.scheduleOrientationClient.scheduleOrientationDELETE(contractId));
    }

    // ** ScheduleOrientationService.getFacilityScheduleOrientationPermission, ScheduleOrientationController.GetFacilityScheduleOrientationPermission
    getFacilityScheduleOrientationPermission(facilityId: number): Observable<number> {
        return this.invoke(x => x
            ? this.legacy.getFacilityScheduleOrientationPermission(facilityId)
            : this.scheduleOrientationClient.getFacilityScheduleOrientationPermission(facilityId));
    }

    // ** ScheduleOrientationService.getScheduleOrientationSessionTypes, ScheduleOrientationController.GetScheduleOrientationSessionTypes
    getScheduleOrientationSessionTypes(): Observable<ScheduleOrientationSessionType[] | ScheduleOrientationSessionTypeDto[]> {
        return this.invoke(x => x
            ? this.legacy.getScheduleOrientationSessionTypes()
            : this.scheduleOrientationClient.getScheduleOrientationSessionTypes());
    }

    // ** ScheduleOrientationService.cancelScheduleOrientation, ScheduleOrientationController.CancelScheduleOrientation
    cancelScheduleOrientation(scheduleOrientationId: number): Observable<ScheduleOrientation | ScheduleOrientationDto> {
        return this.invoke(x => x
            ? this.legacy.cancelScheduleOrientation(scheduleOrientationId)
            : this.scheduleOrientationClient.cancelScheduleOrientation(scheduleOrientationId));
    }

    // ** ScheduleOrientationService.patchUpdateNote, ScheduleOrientationController.UpdateNote
    patchUpdateNote(scheduleOrientationId: number, note: string): Observable<void> {
        return this.invoke(x => x
            ? this.legacy.patchUpdateNote(scheduleOrientationId, note)
            : this.scheduleOrientationClient.updateNote(scheduleOrientationId, note));
    }

    // ** ScheduleOrientationService.patchSendOrientationToMyAya, ScheduleOrientationController.SendOrientationToMyAya
    patchSendOrientationToMyAya(scheduleOrientationId: number): Observable<ScheduleOrientation | ScheduleOrientationDto> {
        return this.invoke(x => x
            ? this.legacy.patchSendOrientationToMyAya(scheduleOrientationId)
            : this.scheduleOrientationClient.sendOrientationToMyAya(scheduleOrientationId));
    }

    // ** ScheduleOrientationService.postRescheduleOrientation, ScheduleOrientationController.RescheduleOrientation
    postRescheduleOrientation(scheduleOrientationId: number): Observable<ScheduleOrientation | ScheduleOrientationDto> {
        return this.invoke(x => x
            ? this.legacy.postRescheduleOrientation(scheduleOrientationId)
            : this.scheduleOrientationClient.rescheduleOrientation(scheduleOrientationId));
    }

    // ** ScheduleOrientationService.postAskForNewAvailabilityOrientation, ScheduleOrientationController.AskForNewAvailability
    postAskForNewAvailabilityOrientation(scheduleOrientationId: number): Observable<ScheduleOrientation | ScheduleOrientationDto> {
        return this.invoke(x => x
            ? this.legacy.postAskForNewAvailabilityOrientation(scheduleOrientationId)
            : this.scheduleOrientationClient.askForNewAvailability(scheduleOrientationId));
    }

    // ** ScheduleOrientationService.getNextDayForSessionType, ScheduleOrientationController.GetNextDayForSessionType
    getNextDayForSessionType(contractId: number, sessionTypeId: number | ScheduleOrientationSessionTypesEnum): Observable<number> {
        return this.invoke(x => x
            ? this.legacy.getNextDayForSessionType(contractId, sessionTypeId)
            : this.scheduleOrientationClient.getNextDayForSessionType(contractId, sessionTypeId));
    }

    // ** ScheduleOrientationService.doesAnyOrientationExist, ScheduleOrientationController.DoesAnyOrientationExist
    doesAnyOrientationExist(contractId: number): Observable<boolean> {
        return this.invoke(x => x
            ? this.legacy.doesAnyOrientationExist(contractId)
            : this.scheduleOrientationClient.doesAnyOrientationExist(contractId));
    }

    // ** ScheduleOrientationService.GetMultiBundlesStatuses, ScheduleOrientationController.GetMultiBundlesStatuses
    GetMultiBundlesStatuses(contractId: number): Observable<LegacyMultiBundleStatus[] | MultiBundleStatus[]> {
        return this.invoke(x => x
            ? this.legacy.GetMultiBundlesStatuses(contractId)
            : this.scheduleOrientationClient.getMultiBundlesStatuses(contractId));
    }

    // ** ScheduleOrientationService.putScheduleOrientationAvailability, ScheduleOrientationController.UpdateOrientationAvailability
    putScheduleOrientationAvailability(orientation: UpdateOrientationDay | UpdateOrientationDto): Observable<ScheduleOrientation | ScheduleOrientationDto> {
        return this.invoke(x => x
            ? this.legacy.putScheduleOrientationAvailability(orientation)
            : this.scheduleOrientationClient.updateOrientationAvailability(orientation as UpdateOrientationDto));
    }

    // ** ScheduleOrientationService.patchPresetDatesOfScheduleOrientation, ScheduleOrientationController.UpdatePresetDatesOfOrientation
    patchPresetDatesOfScheduleOrientation(presetDateDto: ScheduleOrientationPresetDate | ScheduleOrientationPresetDateDto): Observable<ScheduleOrientation | ScheduleOrientationDto> {
        return this.invoke(x => x
            ? this.legacy.patchPresetDatesOfScheduleOrientation(presetDateDto)
            : this.scheduleOrientationClient.updatePresetDatesOfOrientation(presetDateDto as ScheduleOrientationPresetDateDto));
    }

    // ** ScheduleOrientationService.patchNoteOfScheduleOrientation, ScheduleOrientationController.UpdateNoteOfOrientation
    patchNoteOfScheduleOrientation(scheduleOrientationId: number, note: string): Observable<ScheduleOrientation | ScheduleOrientationDto> {
        return this.invoke(x => x
            ? this.legacy.patchNoteOfScheduleOrientation(scheduleOrientationId, note)
            : this.scheduleOrientationClient.updateNoteOfOrientation(scheduleOrientationId, note));
    }

    // ** ScheduleOrientationService.putScheduleOrientationMultiDayForCandidate, ScheduleOrientationController.ScheduleMultiDayOrientations
    putScheduleOrientationMultiDayForCandidate(
        firstDayOrientationId: number,
        candidateAvailabilityDateArray: CandidateAvailabilityDate[] | CandidateAvailabilityDateDto[]): Observable<ScheduleOrientation | ScheduleOrientationDto> {
        return this.invoke(x => x
            ? this.legacy.putScheduleOrientationMultiDayForCandidate(firstDayOrientationId, candidateAvailabilityDateArray)
            : this.scheduleOrientationClient.scheduleMultiDayOrientations(firstDayOrientationId, candidateAvailabilityDateArray));
    }

    // ** ScheduleOrientationService.putMultiDayAskForNewAvailabilityDates, ScheduleOrientationController.MultiDayAskForNewAvailability
    putMultiDayAskForNewAvailabilityDates(
        firstDayOrientationId: number,
        orientationDays: UpdateOrientationDay[] | UpdateOrientationDto[]): Observable<ScheduleOrientation | ScheduleOrientationDto> {
        return this.invoke(x => x
            ? this.legacy.putMultiDayAskForNewAvailabilityDates(firstDayOrientationId, orientationDays)
            : this.scheduleOrientationClient.multiDayAskForNewAvailability(firstDayOrientationId, orientationDays as UpdateOrientationDto[]));
    }

    // ** ScheduleOrientationService.getExistingOrientationForSessionType, ScheduleOrientationController.GetExistingOrientationForSessionType
    getExistingOrientationForSessionType(contractId: number, sessionTypeId: number): Observable<ScheduleOrientation[] | ScheduleOrientationDto[]> {
        return this.invoke(x => x
            ? this.legacy.getExistingOrientationForSessionType(contractId, sessionTypeId)
            : this.scheduleOrientationClient.getExistingOrientationForSessionType(contractId, sessionTypeId));
    }

    // ** ScheduleOrientationService.postRescheduleMultiDayOrientations, ScheduleOrientationController.RescheduleMultiDayOrientations
    postRescheduleMultiDayOrientations(firstDayOrientationId: number, listOfOrientationIds: number[]): Observable<ScheduleOrientation | ScheduleOrientationDto> {
        return this.invoke(x => x
            ? this.legacy.postRescheduleMultiDayOrientations(firstDayOrientationId, listOfOrientationIds)
            : this.scheduleOrientationClient.rescheduleMultiDayOrientations(firstDayOrientationId, listOfOrientationIds));
    }

    // ** ScheduleOrientationService.patchSendMultiDayOrientationToMyAya, ScheduleOrientationController.SendMultiDayOrientationToMyAya
    patchSendMultiDayOrientationToMyAya(firstDayOrientationId: number, listOfOrientationIds: number[]): Observable<ScheduleOrientation | ScheduleOrientationDto> {
        return this.invoke(x => x
            ? this.legacy.patchSendMultiDayOrientationToMyAya(firstDayOrientationId, listOfOrientationIds)
            : this.scheduleOrientationClient.sendMultiDayOrientationToMyAya(firstDayOrientationId, listOfOrientationIds));
    }
}
